import { useRef, useState, useEffect } from 'react';

import { localStorageGetItem } from 'src/utils/local-storage';

export interface PresentationPreview {
  /** An object URL for the PDF, or null until the first one arrives. */
  url: string | null;
  loading: boolean;
  /** True when the last request failed; the previous PDF stays on screen. */
  failed: boolean;
  /** The server's reason when it gave one, such as the document converter being off. */
  errorCode?: string;
}

/**
 * The live preview (JT1-1886): the exported PDF itself, fetched for each saved version. RTK Query
 * doesn't handle binary bodies well, so this uses fetch, as the profile export does. A newer
 * version cancels the request still in flight, and the old object URL is released.
 */
export function usePresentationPreview(id: string | undefined, version: number | undefined): PresentationPreview {
  const [state, setState] = useState<PresentationPreview>({ url: null, loading: false, failed: false });
  const current = useRef<string | null>(null);

  useEffect(() => {
    if (!id || !version) {
      return undefined;
    }
    const controller = new AbortController();
    setState((previous) => ({ ...previous, loading: true, failed: false }));

    fetch(`${import.meta.env.VITE_BACKEND_URL}/my/presentations/${id}/preview`, {
      method: 'POST',
      signal: controller.signal,
      headers: { Authorization: `Bearer ${localStorageGetItem('access_token', undefined)}`, Accept: 'application/pdf' },
    })
      .then(async (response) => {
        if (!response.ok) {
          const body = await response.json().catch(() => null);
          throw Object.assign(new Error(`preview ${response.status}`), { code: body?.error_code as string | undefined });
        }
        const url = URL.createObjectURL(await response.blob());
        if (current.current) URL.revokeObjectURL(current.current);
        current.current = url;
        setState({ url, loading: false, failed: false, errorCode: undefined });
      })
      .catch((error) => {
        if (error?.name === 'AbortError') return;
        setState((previous) => ({ ...previous, loading: false, failed: true, errorCode: error?.code }));
      });

    return () => controller.abort();
  }, [id, version]);

  // Release the last URL when the builder closes.
  useEffect(() => () => {
    if (current.current) URL.revokeObjectURL(current.current);
  }, []);

  return state;
}

/** Downloads a file from a signed URL without leaving the page. */
export function downloadFromUrl(url: string, fileName: string) {
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', fileName);
  link.rel = 'noopener';
  document.body.appendChild(link);
  link.click();
  link.remove();
}
